import { useEffect, useState } from "react";
import LaredoHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Laredo?", a: "We offer Laredo-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes. We review your documents before submission to catch errors that cause delays." },
  { q: "Do I need to visit an office in Laredo?", a: "No. We do not have a physical office in Laredo. Our service is remote: you complete forms online, visit a local acceptance facility (like a Laredo post office) for in-person verification, then ship documents to us via overnight courier. We handle the expedited submission at the State Department and return your passport to your Laredo address." },
  { q: "Where is the nearest passport acceptance facility in Laredo?", a: "Laredo has multiple acceptance facilities including post offices (Downtown, North Laredo, Del Mar), Webb County offices, and select libraries. We provide a curated list of Laredo-area facilities with address, phone, and photo availability. First-time applicants and minors must visit in person; renewals typically ship directly." },
  { q: "Can I get a same-day passport near Laredo?", a: "The nearest passport agencies are in San Antonio (150 miles) and Houston (330 miles). Walk-ins at these agencies require proof of imminent travel within 14 days and an appointment. Our registered courier service offers the fastest legally available channel — as quick as 24 hours — without requiring a long drive." },
  { q: "Does Laredo border position affect my passport needs?", a: "Yes. While a passport card suffices for land border crossings between Laredo and Nuevo Laredo, air travel to Mexico City, Monterrey, and all other international destinations requires a full passport book. Many Laredo residents who only cross by land do not realize they need a book for air travel until booking time." },
  { q: "What documents do I need for a Laredo passport application?", a: "For a new adult passport: proof of citizenship (certified birth certificate or naturalization certificate), government-issued photo ID, one passport photo, and the completed DS-11 form. For renewals: your most recent passport, one photo, and the DS-82 form. For minors: both parents must appear (or provide consent), plus the child's birth certificate and photos." },
  { q: "Can I get a passport photo in Laredo?", a: "Yes. Many Laredo post offices, CVS, Walgreens, and Walmart locations offer passport photos. We recommend confirming photo availability before visiting. Passport photos must meet strict State Department specifications — 2x2 inches, white background, no glasses, neutral expression." },
  { q: "What is the difference between a passport card and a passport book for Laredo residents?", a: "Laredo residents who regularly cross the border to Nuevo Laredo often use a passport card, which is valid for land crossings and some sea crossings. A passport book is required for air travel to Mexico and all international destinations. If you need to fly internationally, you need the book. If you drive across the bridge to Nuevo Laredo, the card is sufficient." },
  { q: "Can I track my Laredo passport application?", a: "Yes. Once we submit your application, we provide tracking information. You can also check the State Department's online passport status system. Our courier service includes tracking at every stage: from Laredo to our processing center, through State Department review, and return shipping to your Texas address." },
  { q: "What if my passport is lost or stolen in Laredo?", a: "File a police report if stolen, then complete Form DS-64 (Statement Regarding Lost or Stolen Passport) along with Form DS-11 for a replacement. You will need to visit an acceptance facility in person and provide citizenship documentation again. If you have upcoming travel, our expedited service can process the replacement as fast as 24 hours." },
];

function LaredoStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActive(id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" },
    { id: "services", label: "Services" },
    { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" },
    { id: "destinations", label: "Destinations" },
    { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" },
    { id: "comparison", label: "Compare" },
    { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" },
    { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>
        ))}
      </div>
    </div>
  );
}

function LaredoProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Laredo Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Laredo-area travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Laredo travelers with imminent departure within 3 days. Includes same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For trade workers and families with travel in 1–2 weeks. Balanced speed and value with full document review." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Laredo residents planning ahead. Still faster than standard post office processing (10–13 weeks) with professional document handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LaredoServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Laredo travelers with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Laredo residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Laredo adults with acceptance facility support." },
    { icon: "ri-bank-card-line", title: "Passport Card", desc: "Passport card applications for Laredo land border crossers who also need the book for air travel." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Laredo families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Laredo residents." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors that delay Laredo applications." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Laredo Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for Laredo's unique binational community — from international trade workers to cross-border families to Mexico heritage travelers.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LaredoLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Laredo</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Laredo is the largest inland port in the United States — a city of 270,000 residents where international trade is not just an economic sector but the defining identity of the community. The Port of Laredo handles over $200 billion in annual trade between the United States and Mexico, making it the nation's busiest land port of entry. From customs brokers to international freight forwarders to warehouse logistics managers, Laredo's workforce is fundamentally cross-border in character.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The Laredo-Nuevo Laredo metropolitan area is a true binational city. Residents move between both sides of the Rio Grande regularly for family, work, and commerce. While a passport card is sufficient for most land crossings, the growing need to attend business conferences in Mexico City, fly to international trade shows, and visit family in interior Mexico creates constant demand for passport books. Many Laredo residents hold a card but have never applied for the book — until they book a flight.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Laredo's international trade workforce includes thousands of customs brokers, freight forwarders, trucking company managers, and logistics professionals who travel internationally for industry events, supplier meetings, and customs compliance training. The American Association of Exporters and Importers, the National Customs Brokers and Forwarders Association, and similar organizations hold conferences in Washington D.C., New York, and internationally — requiring current passports with often 2–3 weeks notice.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Laredo is over 95% Hispanic and one of the most culturally Mexican cities in the United States. Family heritage travel to Nuevo León, Coahuila, Tamaulipas, and deeper into Mexico's interior is a year-round tradition. Extended family groups traveling together for quinceañeras, baptisms, and anniversaries need every member to have a current passport — creating multi-person urgent processing needs that we handle regularly.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            The nearest passport agencies are in San Antonio (150 miles) and Houston (330 miles). For Laredo residents with urgent needs, driving to an agency is a half-day or full-day commitment. Our remote service solves this geographic isolation problem, offering Laredo the same expedited processing speed as residents in San Antonio or Houston.
          </p>
        </div>
      </div>
    </section>
  );
}

function LaredoTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Laredo Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">LRD — Laredo International Airport</h3>
                <p className="text-gray-400 text-xs">Regional connections to Houston, Dallas, and San Antonio</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Laredo International Airport offers connections to Houston Intercontinental and Dallas/Fort Worth, from where Laredo travelers access the full range of international destinations. Many Laredo residents drive to San Antonio International (2.5 hours) for direct international flights or better connections. For business travel, the time investment to reach a major hub means passport issues discovered late are particularly costly. Our expedited service helps Laredo travelers avoid missing flights due to passport problems.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Laredo residents who drive to San Antonio or Houston airports should verify their passport 2–3 months before international travel. The drive time adds significant cost to any last-minute passport emergency.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-truck-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Port of Laredo &amp; International Trade</h3>
                <p className="text-gray-400 text-xs">$200+ billion annual trade, largest inland U.S. port</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Port of Laredo processes more cross-border trade than any other inland U.S. port of entry. Customs brokers, freight forwarders, and logistics professionals fly to Mexico City, Washington D.C., and international trade conferences regularly. These professionals often receive conference invitations with 2–3 weeks notice, and their industry requires them to attend. A passport expiring before a critical trade association event is one of our most common Laredo urgent requests.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Trade professionals who travel internationally for conferences should set a calendar reminder 9 months before passport expiration. Industry conferences are often announced with less than 6 weeks notice.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-contacts-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Cross-Border Families</h3>
                <p className="text-gray-400 text-xs">Binational families with relatives in Nuevo Laredo and beyond</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Laredo's binational character means many residents have immediate family members on both sides of the Rio Grande. Cross-border family events — weddings, funerals, graduations, and baptisms — require passports that are current and valid. A family emergency on the Mexican side requiring air travel to Monterrey or Mexico City creates urgent passport demand with essentially zero notice. We process Laredo family emergency passport requests regularly.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Keep your passport book current even if you primarily use a passport card for daily border crossings. A family emergency requiring air travel to Mexico may not allow time for expedited processing.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Texas A&amp;M International &amp; Academic Travel</h3>
                <p className="text-gray-400 text-xs">University with strong international focus</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Texas A&M International University in Laredo has a strong focus on international trade and business, sending students and faculty to Mexico and beyond for study abroad, research programs, and academic conferences. TAMIU's location in Laredo gives it a unique international character, with many students who are themselves cross-border or binational. Faculty traveling to academic conferences in Latin America, Europe, and Asia need current passports and often discover expiration issues when their travel is approved.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> TAMIU students applying for study abroad should verify passport expiration when submitting their program application — not when their departure date is confirmed.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-map-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Laredo — Remote from Passport Agencies</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              The nearest passport agency is in San Antonio, 150 miles away. For urgent passport needs, driving to the agency requires a half-day commitment with no guarantee of an appointment. Our remote expedited service provides Laredo residents the same processing speed without the road trip — we come to you via overnight courier.
            </p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function LaredoDestinationsSection() {
  const destinations = [
    { name: "Mexico City", context: "Trade industry conferences, customs compliance training, freight industry events and government meetings", urgency: "Industry events are often announced 2–3 weeks out with immovable dates", icon: "ri-map-pin-2-line" },
    { name: "Monterrey", context: "Manufacturing plant visits, business meetings, family heritage travel to Nuevo León", urgency: "Business meetings and family events have fixed dates that cannot be rescheduled", icon: "ri-map-pin-2-line" },
    { name: "Cancun & Riviera Maya", context: "Family vacations, honeymoon travel, spring break resort trips from Laredo families", urgency: "Vacation bookings peak January-March; passport checks happen after booking", icon: "ri-map-pin-2-line" },
    { name: "Washington D.C.", context: "Trade association annual conventions, customs broker certification events, government affairs meetings", urgency: "Annual conferences have year-round planning but passport checks happen too late", icon: "ri-map-pin-2-line" },
    { name: "Europe", context: "Trade fair travel (Germany automotive, France luxury goods), heritage tourism", urgency: "European business travel is planned 3–4 weeks out but passport validity often overlooked", icon: "ri-map-pin-2-line" },
    { name: "Interior Mexico", context: "Heritage family travel to Coahuila, Tamaulipas, Jalisco, Michoacan", urgency: "Family milestone events (weddings, baptisms) have fixed dates requiring air travel", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "#1", label: "busiest inland port in the United States — $200+ billion in annual US-Mexico trade" },
    { stat: "150 Miles", label: "to the nearest passport agency in San Antonio, making remote service essential for Laredo residents" },
    { stat: "Dec & Apr", label: "peak passport demand when Christmas family travel and spring trade conferences overlap" },
    { stat: "48 Hours", label: "fastest turnaround for a Laredo customs broker with an emergency Mexico City conference" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Laredo Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/san-antonio" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">San Antonio <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/corpus-christi" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Corpus Christi <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/el-paso" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">El Paso <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LaredoTestimonialsSection() {
  const testimonials = [
    { name: "Ricardo M.", location: "Del Mar Heights", text: "I'm a customs broker and had an emergency conference in Mexico City with 5 days notice. My passport was expired. Fast Passport Center had it processed in 48 hours. They literally saved my career standing.", rating: 5 },
    { name: "Maria E.", location: "North Laredo", text: "My mother had a medical emergency in Monterrey and I needed to fly immediately. My passport was expired. They processed it in 2 days and I made it to be with my family. Incredibly grateful.", rating: 5 },
    { name: "The Garza Family", location: "Laredo West", text: "We were taking 8 family members to a wedding in Jalisco and two passports had expired. They processed both and had them back in 6 days. Everyone made the wedding.", rating: 5 },
    { name: "Dr. Sofia T.", location: "TAMIU Area", text: "My research conference in Spain came up with 3 weeks notice and my passport had water damage. They handled the replacement and I made my presentation. Professional service from start to finish.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Laredo Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LaredoAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Laredo Main Post Office", address: "2310 Clark Blvd, Laredo, TX 78043", phone: "(956) 727-8900", photo: "Yes", appointment: "Required" },
    { name: "Del Mar Station Post Office", address: "600 Del Mar Blvd, Laredo, TX 78041", phone: "(956) 727-8900", photo: "Yes", appointment: "Walk-in" },
    { name: "North Laredo Post Office", address: "7700 McPherson Rd, Laredo, TX 78045", phone: "(956) 727-8900", photo: "Yes", appointment: "Required" },
    { name: "Webb County Clerk", address: "1110 Victoria St, Laredo, TX 78040", phone: "(956) 523-4268", photo: "No", appointment: "Required" },
    { name: "United Station Post Office", address: "120 Bob Bullock Loop, Laredo, TX 78041", phone: "(956) 727-8900", photo: "Yes", appointment: "Required" },
    { name: "Rio Bravo Post Office", address: "200 Jaime Zapata Memorial Hwy, Rio Bravo, TX 78046", phone: "(956) 765-4536", photo: "Yes", appointment: "Walk-in" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Laredo Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are Laredo-area options with contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. We recommend calling ahead to confirm availability.</p>
        </div>
      </div>
    </section>
  );
}

function LaredoComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Laredo Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Drive to San Antonio Agency</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", drive: "Same-day if appointment available" },
                { feature: "Distance Required", us: "None — remote service", post: "Local post office", drive: "150 miles one-way" },
                { feature: "Document Review", us: "Included", post: "None", drive: "None" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", drive: "Government fees + fuel + hotel" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", drive: "Government direct" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", drive: "Agency staff only" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", drive: "You pick up in person" },
                { feature: "BBB Rating", us: "A+", post: "N/A", drive: "N/A" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.drive}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function LaredoMistakesSection() {
  const mistakes = [
    { title: "Only Having a Passport Card", desc: "Many Laredo residents use a passport card for daily border crossings. But if you need to fly to Mexico City, Monterrey, or anywhere internationally, you need a passport book. Discover this before booking, not at the airport." },
    { title: "Driving to San Antonio Without an Appointment", desc: "Many Laredo residents attempt to drive to the San Antonio passport agency without an appointment. Without proof of imminent travel within 14 days, walk-ins are turned away after a 150-mile drive." },
    { title: "Unacceptable Passport Photos", desc: "Many Laredo photo locations are not trained on current State Department photo standards. A rejected photo adds 1–2 weeks to your processing time." },
    { title: "Forgetting Parental Consent for Children", desc: "For minors, both parents must appear or provide notarized consent. Single-parent applications missing the DS-3053 form are rejected outright." },
    { title: "Not Keeping the Book Current", desc: "Laredo residents who cross by land regularly often let their passport book expire while keeping the card current. An expired book means no air travel to Mexico or internationally until it is renewed." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Laredo Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LaredoHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our Laredo-specific intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Laredo-area acceptance facility. We provide the nearest location with available appointments." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application package via overnight courier to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit your application through our registered courier channel with the State Department. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your Laredo address via secure overnight shipping. No 150-mile drive to San Antonio required." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Laredo Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LaredoFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Laredo Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LaredoHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Laredo Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <a href="https://travel.state.gov/content/travel/en/passports.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-government-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">U.S. State Department</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Official passport information, forms, and processing status updates.</p>
          </a>
          <a href="https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-earth-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Travel Advisories</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Current safety and entry requirements for every country.</p>
          </a>
          <a href="https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-visa-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Visa Waiver Program</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Check if your destination participates in the ESTA visa waiver program.</p>
          </a>
        </div>
      </div>
    </section>
  );
}

function LaredoFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Laredo Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">
          Whether you have an emergency trade conference in Mexico City in 48 hours or a family wedding in Jalisco in 3 weeks, we will get your passport processed on time — no 150-mile drive required. No surprises. No delays.
        </p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function LaredoPage() {
  useEffect(() => {
    document.title = "Emergency Passport Laredo, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/laredo/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Laredo, TX — as fast as 24 hours. No need to drive 150 miles to San Antonio. A+ BBB rated, registered State Dept. courier serving customs brokers, international trade workers, and Laredo's binational families.");
    setMeta("geo.placename", "Laredo, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "27.5035;-99.5075");
    setMeta("ICBM", "27.5035, -99.5075");
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "Laredo Passport Services", item: pageUrl },
      ]},
      { "@type": "LocalBusiness", name: "Fast Passport Center", description: "Emergency and expedited passport services as fast as 24 hours for Laredo and Webb County travelers.", url: pageUrl, telephone: "+1-877-253-0084", areaServed: { "@type": "City", name: "Laredo", containedInPlace: { "@type": "State", name: "Texas" } }, priceRange: "$$$", openingHours: ["Mo-Fr 09:00-18:00"], aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" }},
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <LaredoHeroSection />
      <LaredoStickyNav />
      <LaredoProcessingTimeSection />
      <LaredoServicesSection />
      <LaredoLocalAreaSection />
      <LaredoTravelTimingSection />
      <LaredoDestinationsSection />
      <LaredoTestimonialsSection />
      <LaredoAcceptanceFacilitiesSection />
      <LaredoComparisonSection />
      <LaredoMistakesSection />
      <LaredoHowItWorksSection />
      <LaredoFAQSection />
      <LaredoHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/laredo" state="TX" />
      <LaredoFinalCTA />
    </div>
  );
}